import { Link, Navigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import { useAuth } from "@/context/AuthContext";
import AutoBlogQueue from "@/components/app/AutoBlogQueue";
import OpportunityRadar from "@/components/app/OpportunityRadar";
import ConversionRadar from "@/components/app/ConversionRadar";
import AdminPlanSwitcher from "@/components/app/AdminPlanSwitcher";
import { Activity, ShieldCheck, ArrowUpRight } from "lucide-react";

/**
 * Admin Console — operator dashboard for the ArloSEO owner.
 *
 * Hidden from non-admins via route guard. Aggregates business-operations
 * widgets (Arlo's auto-blog queue, Opportunity Radar, Conversion Radar)
 * that used to leak onto the user-facing Projects page.
 */
export default function AdminConsole() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
              // operator
            </p>
            <h1 className="font-semibold tracking-tight text-4xl md:text-5xl mt-2 flex items-center gap-3">
              <ShieldCheck className="w-9 h-9" strokeWidth={1.5} />
              Admin.
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Internal tools for running ArloSEO as a business. Not visible to regular users.
            </p>
          </div>
          <Link
            to="/admin/metrics"
            className="inline-flex items-center gap-2 border border-foreground px-4 h-11 hover:bg-foreground hover:text-background transition-colors"
            data-testid="admin-link-metrics"
          >
            <Activity className="w-4 h-4" />
            <span className="font-mono-data text-xs uppercase tracking-widest">Metrics & Observability</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <AdminPlanSwitcher />
        <AutoBlogQueue user={user} />
        <OpportunityRadar user={user} />
        <ConversionRadar user={user} />
      </main>
    </div>
  );
}
